import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Video, Code2, Copy, Trash2, CheckSquare, Square, ArrowUp, ArrowDown, ArrowUpDown, Columns3, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import { StatusChip, jobLocation, fmtPosted, JOB_STATUSES } from "@/components/app/JobCard";
import { fmtSalaryBand } from "@/lib/countries";

const ROUNDS = [["telephonic", Phone, "Telephonic"], ["video", Video, "Video"], ["coding", Code2, "Coding"]];
const TH = "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap";
const TD = "px-3 py-3 align-middle";
const STATUS_ORDER = Object.fromEntries(JOB_STATUSES.map((s, i) => [s.key, i]));
const num = (j, f) => f(j) || 0;

const COLS = [
  { k: "title", label: "Job", fixed: true, sort: (j) => (j.title || "").toLowerCase(), cls: "max-w-[320px]",
    cell: (j) => (<>
      <div className="font-medium text-slate-900 truncate" data-testid={`row-job-title-${j.id}`}>{j.title}</div>
      {(j.client || j.account || j.job_type) && (
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
          <span className="truncate">{[j.client, j.account].filter(Boolean).join(" · ")}</span>
          {j.job_type && <span className={`shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${j.job_type === "fte" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{j.job_type === "fte" ? "Full Time" : "Contract"}</span>}
        </div>
      )}
    </>) },
  { k: "status", label: "Status", sort: (j) => STATUS_ORDER[j.status] ?? 1, cls: "whitespace-nowrap", cell: (j) => <StatusChip status={j.status} testid={`row-job-status-${j.id}`} /> },
  { k: "location", label: "Location", sort: (j) => jobLocation(j).toLowerCase(), cls: "text-slate-600 whitespace-nowrap", cell: (j) => jobLocation(j) || "—" },
  { k: "rounds", label: "Rounds", cell: (j) => <div className="flex gap-1">{ROUNDS.filter(([k]) => j.stages?.[k]).map(([k, Icon, l]) => <span key={k} title={l} className="w-6 h-6 rounded-md bg-slate-100 text-slate-600 grid place-items-center"><Icon className="w-3.5 h-3.5" /></span>)}</div> },
  { k: "candidates", label: "Candidates", right: true, sort: (j) => num(j, (x) => x.candidate_count), cls: "font-semibold text-slate-900", cell: (j) => j.candidate_count || 0 },
  { k: "completed", label: "Completed", right: true, sort: (j) => num(j, (x) => x.pipeline?.shortlisted), cls: "font-semibold text-emerald-600", cell: (j) => j.pipeline?.shortlisted || 0 },
  { k: "in_progress", label: "In progress", right: true, sort: (j) => num(j, (x) => x.pipeline?.in_progress), cls: "font-semibold text-indigo-600", cell: (j) => j.pipeline?.in_progress || 0 },
  { k: "salary", label: "Salary", sort: (j) => Number(j.salary_max || j.salary_min || 0), cls: "text-slate-600 whitespace-nowrap", cell: (j) => fmtSalaryBand(j.salary_min, j.salary_max, j.salary_currency, j.salary_unit) || "—" },
  { k: "posted", label: "Posted", sort: (j) => j.created_at || "", cls: "text-slate-500 whitespace-nowrap", cell: (j) => fmtPosted(j.created_at).replace("Posted ", "") },
];
const DEFAULT_HIDDEN = [];

function usePrefs(userId) {
  const key = `rs_jobs_rows_${userId || "anon"}`;
  const [p, setP] = useState(() => { try { return { hidden: DEFAULT_HIDDEN, sort: { k: "posted", dir: "desc" }, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch (_) { return { hidden: DEFAULT_HIDDEN, sort: { k: "posted", dir: "desc" } }; } });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(p)); } catch (_) {} }, [key, p]);
  return [p, setP];
}

function ColumnChooser({ hidden, setHidden }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="column-chooser-btn" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-[color:var(--bd-sub)] bg-[color:var(--bg-card)] text-slate-700 hover:bg-slate-50 transition-colors">
          <Columns3 className="w-4 h-4" /> Columns{hidden.length ? ` (${COLS.length - hidden.length}/${COLS.length})` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2 bg-white text-slate-700 border-slate-200" data-testid="column-chooser">
        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Show columns</div>
        {COLS.map((c) => (
          <label key={c.k} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm ${c.fixed ? "opacity-60" : "hover:bg-slate-50 cursor-pointer"}`}>
            <Checkbox data-testid={`column-toggle-${c.k}`} checked={!hidden.includes(c.k)} disabled={c.fixed}
              onCheckedChange={(v) => setHidden(v ? hidden.filter((k) => k !== c.k) : [...hidden, c.k])} />
            {c.label}
          </label>
        ))}
        <button data-testid="column-chooser-reset" onClick={() => setHidden(DEFAULT_HIDDEN)} className="mt-1 w-full inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-indigo-600 hover:bg-indigo-50">
          <RotateCcw className="w-3.5 h-3.5" /> Show all columns
        </button>
      </PopoverContent>
    </Popover>
  );
}

function SortHead({ c, sort, onSort }) {
  const on = sort.k === c.k;
  const Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH} ${c.right ? "text-right" : "text-left"}`} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      {c.sort ? (
        <button data-testid={`sort-${c.k}`} onClick={() => onSort(c.k)} className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${on ? "text-indigo-600" : "hover:text-slate-800"}`}>
          {c.label}<Icon className={`w-3 h-3 ${on ? "" : "opacity-40"}`} />
        </button>
      ) : c.label}
    </th>
  );
}

export default function JobsList({ jobs, selected, toggleOne, canCreate, canDelete, onDuplicate, onDelete }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [prefs, setPrefs] = usePrefs(user?.id);
  const cols = COLS.filter((c) => !prefs.hidden.includes(c.k));
  const onSort = (k) => setPrefs((p) => ({ ...p, sort: { k, dir: p.sort.k === k && p.sort.dir === "desc" ? "asc" : "desc" } }));
  const rows = useMemo(() => {
    const c = COLS.find((x) => x.k === prefs.sort.k) || COLS[COLS.length - 1];
    const m = prefs.sort.dir === "asc" ? 1 : -1;
    const empty = (v) => v === "" || (c.k === "salary" && !v);
    return [...jobs].sort((a, b) => {
      const x = c.sort(a), y = c.sort(b);
      if (empty(x) !== empty(y)) return empty(x) ? 1 : -1;
      return x < y ? -m : x > y ? m : 0;
    });
  }, [jobs, prefs.sort]);
  const stop = (fn, j) => (e) => { e.stopPropagation(); fn(j); };

  return (
    <div data-testid="jobs-list">
      <div className="flex justify-end mb-2"><ColumnChooser hidden={prefs.hidden} setHidden={(hidden) => setPrefs((p) => ({ ...p, hidden }))} /></div>
      <div className="tp-card overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 420 + cols.length * 80 }}>
          <thead className="bg-slate-50 border-b border-[color:var(--bd-sub)]">
            <tr>
              {canDelete && <th className={`${TH} w-10`} />}
              {cols.map((c) => <SortHead key={c.k} c={c} sort={prefs.sort} onSort={onSort} />)}
              <th className={`${TH} w-20`} />
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--bd-sub)]">
            {rows.map((j) => {
              const sel = selected.has(j.id);
              return (
                <tr key={j.id} data-testid={`job-row-${j.id}`} onClick={() => nav(`/app/jobs/${j.id}`)} className={`cursor-pointer transition-colors hover:bg-slate-50 ${sel ? "bg-indigo-50/60" : ""}`}>
                  {canDelete && (
                    <td className={TD}>
                      <button data-testid={`row-select-job-${j.id}`} aria-pressed={sel} onClick={(e) => { e.stopPropagation(); toggleOne(j.id); }} className="grid place-items-center">
                        {sel ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    </td>
                  )}
                  {cols.map((c) => <td key={c.k} className={`${TD} ${c.right ? "text-right" : ""} ${c.cls || ""}`} data-testid={`row-job-cell-${c.k}-${j.id}`}>{c.cell(j)}</td>)}
                  <td className={TD}>
                    <div className="flex items-center justify-end gap-1">
                      {canCreate && <button data-testid={`row-duplicate-job-${j.id}`} title="Duplicate job" onClick={stop(onDuplicate, j)} className="p-1.5 rounded text-slate-400 hover:text-indigo-500 hover:bg-indigo-500/10 transition-colors"><Copy className="w-4 h-4" /></button>}
                      {canDelete && <button data-testid={`row-delete-job-${j.id}`} title="Delete job" onClick={stop(onDelete, j)} className="p-1.5 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
